import React, { useState, useEffect, useRef } from "react";
import { useSelector } from 'react-redux';
import * as XLSX from "xlsx";
import MaterialTable from 'material-table';
import { Upload, Download, AlertCircle, CheckCircle, X, Trash2 } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

const FIXED_HEADER_MAP = {
  "emp id":                    "empId",
  "emp_id":                    "empId",
  "empid":                     "empId",
  "fe name":                   "feName",
  "fe_name":                   "feName",
  "fename":                    "feName",
  "others convience":          "othersConvenience",
  "others convenience":        "othersConvenience",
  "others_convience":          "othersConvenience",
  "others_convenience":        "othersConvenience",
  "othersconvience":           "othersConvenience",
  "othersconvenience":         "othersConvenience",
  "other convience remarks":   "otherConvenienceRemarks",
  "other convenience remarks": "otherConvenienceRemarks",
  "other_convience_remarks":   "otherConvenienceRemarks",
  "other_convenience_remarks": "otherConvenienceRemarks",
  "otherconvienceremarks":     "otherConvenienceRemarks",
  "otherconvenienceremarks":   "otherConvenienceRemarks",
};

const TEMPLATE_HEADERS = ["Emp ID", "FE Name", "Others Convience", "Other Convience Remarks"];

const ConveyanceMaster = () => {
  const role = useSelector((state) => state.getRoleInfo?.getRoleData || {});
  const [records, setRecords]                 = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [filterMonth, setFilterMonth]         = useState(MONTHS[new Date().getMonth()]);
  const [filterYear, setFilterYear]           = useState(CURRENT_YEAR);
  const [isImportOpen, setIsImportOpen]       = useState(false);
  const [importFile, setImportFile]           = useState(null);
  const [importLoading, setImportLoading]     = useState(false);
  const [importErrors, setImportErrors]       = useState([]);
  const [importSuccess, setImportSuccess]     = useState(0);
  const [companies, setCompanies]             = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [importMonth, setImportMonth]         = useState(MONTHS[new Date().getMonth()]);
  const [importYear, setImportYear]           = useState(CURRENT_YEAR);
  const fileInputRef = useRef(null);

  const fetchCompanies = async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem("userInfo"));
      const res = await fetch(`${API_URL}/company?isActive=true&limit=1000`, {
        headers: { Authorization: `Bearer ${userInfo?.token}` },
      });
      const data = await res.json();
      if (data.companies) setCompanies(data.companies);
    } catch (e) {
      console.error("Failed to fetch companies:", e);
    }
  };

  const fetchRecords = async (month, year) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/convenience-master?month=${encodeURIComponent(month)}&year=${year}`);
      if (!res.ok) { console.error("Failed to fetch:", res.status); return; }
      const data = await res.json();
      if (data.success) {
        setRecords(data.data.map((r, i) => ({ ...r, sNo: i + 1 })));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCompanies(); }, []);
  useEffect(() => { fetchRecords(filterMonth, filterYear); }, [filterMonth, filterYear]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await fetch(`${API_URL}/convenience-master/${id}`, { method: "DELETE" });
      setRecords((prev) =>
        prev.filter((r) => r._id !== id).map((r, i) => ({ ...r, sNo: i + 1 }))
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
    ws["!cols"] = TEMPLATE_HEADERS.map((h) => ({ wch: Math.max(h.length + 4, 20) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Conveyance Master");
    XLSX.writeFile(wb, "conveyance_master_template.xlsx");
  };

  const handleImport = async () => {
    if (!importFile) { alert("Please choose a file."); return; }
    if (!selectedCompany) { alert("Please select a company."); return; }
    setImportLoading(true);
    setImportErrors([]);
    setImportSuccess(0);

    try {
      const arrayBuffer = await importFile.arrayBuffer();
      const wb = XLSX.read(arrayBuffer);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

      const errors = [];
      const batch  = [];

      rows.forEach((row, i) => {
        const rowNum = i + 2;
        const record = {};

        Object.entries(row).forEach(([key, val]) => {
          const field = FIXED_HEADER_MAP[key.trim().toLowerCase()];
          if (field) {
            record[field] = field === "othersConvenience" ? (Number(val) || 0) : val;
          }
        });

        if (!record.empId && !record.feName) {
          errors.push(`Row ${rowNum}: 'Emp ID' or 'FE Name' is required`);
          return;
        }
        record.company = selectedCompany;
        record.month   = importMonth;
        record.year    = Number(importYear);
        batch.push(record);
      });

      if (batch.length > 0) {
        const res = await fetch(`${API_URL}/convenience-master/bulk`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(batch),
        });
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch {
          errors.push(`Server error (${res.status}): unexpected response from server`);
          setImportErrors(errors);
          setImportLoading(false);
          return;
        }
        if (data.success) {
          setImportSuccess(data.count);
          fetchRecords(filterMonth, filterYear);
        } else {
          errors.push(`Server error: ${data.message}`);
        }
      }

      setImportErrors(errors);
    } catch (e) {
      setImportErrors([`Failed to parse file: ${e.message}`]);
    } finally {
      setImportLoading(false);
    }
  };

  const closeImport = () => {
    setIsImportOpen(false);
    setImportFile(null);
    setImportErrors([]);
    setImportSuccess(0);
    setSelectedCompany('');
    setImportMonth(MONTHS[new Date().getMonth()]);
    setImportYear(CURRENT_YEAR);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const cellStyle   = { fontSize: 12 };
  const headerStyle = { fontSize: 12, fontWeight: "bold" };
  const companyMap  = Object.fromEntries(companies.map((c) => [c._id, c.companyName]));

  const columns = [
    { title: "S No",               field: "sNo",                    sorting: false, cellStyle, headerStyle },
    { title: "Company",            field: "company",                cellStyle, headerStyle, render: (row) => companyMap[row.company] || row.company || "—" },
    { title: "Emp ID",             field: "empId",                  cellStyle, headerStyle },
    { title: "FE Name",            field: "feName",                 cellStyle, headerStyle },
    { title: "Others Conveyance",  field: "othersConvenience",      type: "numeric", cellStyle, headerStyle },
    { title: "Conveyance Remarks", field: "otherConvenienceRemarks", cellStyle, headerStyle },
    {
      title: "Actions",
      field: "actions",
      sorting: false,
      cellStyle,
      render: (rowData) => role.conveyanceMasterDelete !== false ? (
        <button
          onClick={() => handleDelete(rowData._id)}
          className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
          title="Delete"
        >
          <Trash2 size={15} />
        </button>
      ) : null,
    },
  ];

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-100">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <h2 className="text-2xl font-bold text-gray-800">Conveyance Master</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="text-sm text-gray-700 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(Number(e.target.value))}
              className="text-sm text-gray-700 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            {role.conveyanceMasterImport !== false && (
              <button
                onClick={() => setIsImportOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition flex items-center gap-2"
              >
                <Upload size={18} />
                Import
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <MaterialTable
            title=""
            columns={columns}
            data={records}
            isLoading={loading}
            options={{
              search: true,
              paging: true,
              pageSize: 15,
              pageSizeOptions: [15, 30, 50],
              emptyRowsWhenPaging: false,
              exportButton: false,
              toolbar: true,
              showTitle: false,
              columnsButton: false,
              tableLayout: "auto",
              headerStyle: { fontWeight: "bold", fontSize: 12, backgroundColor: "#f9fafb", whiteSpace: "nowrap" },
              rowStyle: { height: "48px", fontSize: 12 },
              cellStyle: { whiteSpace: "nowrap" },
            }}
          />
        </div>
      </div>

      {/* Import Modal */}
      {isImportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Upload size={18} className="text-green-600" />
                Import convenience Master
              </h3>
              <button onClick={closeImport} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Template download */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <div>
                  <p className="text-sm font-medium text-gray-700">Excel Template</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Emp ID · FE Name · Others Convience · Other Convience Remarks
                  </p>
                </div>
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium py-1.5 px-3 rounded-lg hover:bg-gray-100 transition"
                >
                  <Download size={15} />
                  Download
                </button>
              </div>

              {/* Company selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="w-full text-sm text-gray-700 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">-- Select Company --</option>
                  {companies.map((c) => (
                    <option key={c._id} value={c._id}>{c.companyName}</option>
                  ))}
                </select>
              </div>

              {/* Month / Year */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Month <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={importMonth}
                    onChange={(e) => setImportMonth(e.target.value)}
                    className="w-full text-sm text-gray-700 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Year <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={importYear}
                    onChange={(e) => setImportYear(Number(e.target.value))}
                    className="w-full text-sm text-gray-700 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>

              {/* File upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Filled File <span className="text-red-500">*</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => {
                    setImportFile(e.target.files[0] || null);
                    setImportErrors([]);
                    setImportSuccess(0);
                  }}
                  className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-green-50 file:text-green-700 file:font-medium hover:file:bg-green-100 border border-gray-300 rounded-lg p-1"
                />
                {importFile && (
                  <p className="text-xs text-gray-500 mt-1">Selected: {importFile.name}</p>
                )}
              </div>

              {/* Success */}
              {importSuccess > 0 && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                  <CheckCircle size={16} />
                  {importSuccess} row{importSuccess > 1 ? "s" : ""} imported successfully.
                </div>
              )}

              {/* Errors */}
              {importErrors.length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg max-h-36 overflow-y-auto">
                  <p className="text-sm font-medium text-red-700 flex items-center gap-1 mb-1">
                    <AlertCircle size={15} /> {importErrors.length} error{importErrors.length > 1 ? "s" : ""}
                  </p>
                  {importErrors.map((e, i) => (
                    <p key={i} className="text-xs text-red-600">{e}</p>
                  ))}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={closeImport}
                  className="flex-1 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={importLoading || !importFile || !selectedCompany}
                  className="flex-1 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {importLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Importing...
                    </>
                  ) : (
                    <><Upload size={15} /> Import</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConveyanceMaster;
